import { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useWriterComponentState } from "hooks";
import { MODAL_STYLE } from "layouts/constants";

import { ClientAppEvent } from "../../../../../../constants";

interface Props {
  note: ShipperShipmentNote;
  onClose: () => void;
  onNoteDeleted: () => void;
  onNoteNotFound: () => void;
}

const MODAL_TITLE_ID = "delete-shipment-note-modal-title";
const NOTE_NOT_FOUND_MESSAGE = "This note does not exist.";

export function DeleteShipmentNoteModal(props: Props) {
  const { note, onClose, onNoteDeleted, onNoteNotFound } = props;
  const posthog = usePostHog();
  const [noteNotFound, setNoteNotFound] = useState<boolean>(false);

  const { loading, error, write, success } = useWriterComponentState({
    errorMessage: "Failed to delete note. Please try again.",
    writeConfig: {
      method: "delete",
      url: `/shipper-shipment-notes/${note.id}`,
    },
    onWriteFailure: (e) => {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        setNoteNotFound(true);
        onNoteNotFound();
      }
    },
  });

  const handleDelete = () =>
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_PRIVATE_NOTE_DELETED, {
        source: "shipment-detail",
      });
      onNoteDeleted();
    });

  return (
    <Modal open onClose={onClose} aria-labelledby={MODAL_TITLE_ID}>
      <Box sx={MODAL_STYLE}>
        <Stack justifyContent="flex-end" direction="row">
          <Tooltip
            title={success ? "Close" : "Close without deleting the note"}
          >
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {success ? (
          <Stack direction="column" spacing={2} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
            <Typography variant="h4" component="h2" id={MODAL_TITLE_ID}>
              Note Deleted
            </Typography>
            <Typography variant="body2" role="status">
              Your private note has been deleted.
            </Typography>
            <Button variant="contained" onClick={onClose} autoFocus>
              Done
            </Button>
          </Stack>
        ) : (
          <Stack direction="column" spacing={2}>
            <Typography
              variant="h4"
              component="h2"
              id={MODAL_TITLE_ID}
              sx={{ mb: "1rem" }}
            >
              Delete This Note?
            </Typography>
            <Typography variant="body1">
              Are you sure you want to delete this note? This action cannot be
              reversed.
            </Typography>
            <Box
              sx={{
                bgcolor: "grey.100",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: "10rem",
                  overflowY: "auto",
                }}
              >
                {`“${note.text}”`}
              </Typography>
            </Box>

            <ErrorTypography
              error={noteNotFound ? NOTE_NOT_FOUND_MESSAGE : error}
            />

            {loading ? (
              <Stack alignItems="center">
                <CircularProgress />
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="text" onClick={onClose}>
                  Cancel
                </Button>
                {!noteNotFound && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
